import axios from "axios";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { ProjectConfig } from 'Global';
import TopBar from "elements/TopBar/TopBar";
import Toolbar from "elements/Toolbar/Toolbar";
import BasicAccordion from "elements/BasicAccordion";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { getFiestasLabel } from "hooks/useLabels";

function SectionAccordion({ title, children, secondary = false }: any) {
  return (
    <Accordion
      disableGutters
      style={{
        width: '100%',
        margin: '0 0 12px',
        backgroundColor: '#FEF5E7',
        borderRadius: 6,
        overflow: 'hidden',
        borderLeft: secondary ? 'none' : '4px solid var(--titulos)',
      }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <strong style={{ fontSize: 16 }}>{title}</strong>
      </AccordionSummary>
      <AccordionDetails style={{ padding: '10px 14px 18px' }}>
        {children}
      </AccordionDetails>
    </Accordion>
  );
}

export default function FiestasViewer(props: any) {
  const lang_id = localStorage.getItem('lang_id') || '1';
  const { fiesta_id } = useParams();

  const defaultValues: any = {
    fiesta_id: fiesta_id || "null",
    image: "",
    titulo: "",
    bioHTML_primeras_visperas: "",
    bioHTML_oficio_lectura: "",
    bioHTML_laudes: "",
    bioHTML_horas_intermedias: "",
    bioHTML_visperas: "",
    bioHTML_completas: "",
    bioHTML_lecturas: "",
    translations: {},
    common_meta: null,
    structured_content: { ready: false, model: null, items: [] },
  };

  const [values, setValues] = useState(defaultValues);
  const [selectedMassGroupKey, setSelectedMassGroupKey] = useState<string | null>(null);
  const [selectedLectionaryOptions, setSelectedLectionaryOptions] = useState<Record<string, number>>({});

  const getLocalizedCommonTitle = () => {
    const meta = values.common_meta;
    if (!meta?.is_common) return '';

    const familyKey = String(meta.family_label_key || '').trim();
    const familyFallback = String(
      meta.family_title || meta.canonical_title || values.titulo || ''
    ).trim();
    const familyLabel = familyKey
      ? getFiestasLabel(familyKey)
      : familyFallback;

    const variantKey = String(meta.variant_key || '').trim();
    if (!variantKey) {
      return familyLabel || familyFallback;
    }

    const variantLabel = getFiestasLabel(`common_variant_${variantKey}`);
    return [familyLabel || familyFallback, variantLabel]
      .filter(Boolean)
      .join(' · ');
  };

  const getTitulo = () => {
    const canonicalCommonTitle = getLocalizedCommonTitle();
    if (canonicalCommonTitle) return canonicalCommonTitle;
    if (lang_id === '1') return values.titulo;
    return values.translations?.[lang_id]?.titulo || values.titulo;
  };

  const getBioHTML = (field: string) => {
    if (lang_id === '1') return values[field];

    if (values.common_meta?.is_common === true) {
      // Los campos legacy de Comunes no conservan procedencia nativa fiable.
      // Para otros idiomas solo se muestran filas estructuradas verificadas.
      return '';
    }

    const translated = values.translations?.[lang_id]?.[field];
    return translated || values[field];
  };

  useEffect(() => {
    props.setLoading(true);

    if (!fiesta_id) {
      props.setLoading(false);
      return;
    }

    axios.get(ProjectConfig.api_url, {
      params: {
        request: "fiesta_details",
        id: fiesta_id,
        lang_id: lang_id,
      },
    })
      .then((response) => {
        setValues(response.data);
        setSelectedMassGroupKey(null);
        setSelectedLectionaryOptions({});
        props.setLoading(false);
      })
      .catch((err) => {
        console.log(err?.data || err);
        props.setLoading(false);
      });
  }, [fiesta_id, lang_id]);

  const emptyHtml = [
    '<p class="PlaygroundEditorTheme__paragraph"><span></span></p>',
    '<p class="PlaygroundEditorTheme__paragraph"><br></p>',
  ];

  const validar = (dato: any) => {
    return dato !== null
      && dato !== ''
      && !emptyHtml.includes(dato);
  };

  const structuredModel =
    values.structured_content?.model || values.common_meta?.content_model || null;

  // fiesta_details ya devuelve structured_content aislado por el lang_id
  // solicitado. En el visor se muestran todas las filas verificadas de ese
  // idioma; no se aplica un segundo filtro de source_key que pueda ocultar
  // contenido real ya certificado por la API/importador.
  const verifiedStructuredItems = (values.structured_content?.items || [])
    .filter(
      (item: any) =>
        item.status === 'verified'
        && Number(item.lang_id) === Number(lang_id)
    );

  // No se permite rellenar huecos con español ni con otra edición lingüística.

  const hasVerifiedLectionary = verifiedStructuredItems.some(
    (item: any) => item.scope === 'lectionary'
  );

  const isCanonicalCommon = values.common_meta?.is_common === true;

  const hourSections = [
    ['bioHTML_primeras_visperas', 'primeras_visperas'],
    ['bioHTML_oficio_lectura', 'oficio_lectura'],
    ['bioHTML_laudes', 'laudes'],
    ['bioHTML_horas_intermedias', 'horas_intermedias'],
    ['bioHTML_visperas', 'visperas'],
  ];

  const legacyReadingSections = !isCanonicalCommon && !hasVerifiedLectionary
    ? [['bioHTML_lecturas', 'lecturas']]
    : [];

  const readableAccordionTitle = (value: string) => {
    const trimmed = String(value || '').trim();
    if (!trimmed) return trimmed;

    if (trimmed === trimmed.toLocaleUpperCase()) {
      const lower = trimmed.toLocaleLowerCase();
      return lower.charAt(0).toLocaleUpperCase() + lower.slice(1);
    }

    return trimmed;
  };

  const escapeHtml = (value: any) =>
    String(value || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');

  const labelFromFiestas = (key: string, fallback: string) => {
    const translated = getFiestasLabel(key);
    const generatedFallback = key.toUpperCase().replace(/_/g, ' ');
    return translated === generatedFallback ? fallback : translated;
  };

  const structuredDefinitionLabel = (definition: any, fallback: string) => {
    const labelKey = String(definition?.label_key || '').trim();
    return labelKey
      ? labelFromFiestas(labelKey, definition?.label || fallback)
      : (definition?.label || fallback);
  };

  const structuredSectionLabel = (scope: string, sectionKey: string) => {
    // Blindaje de interfaz para Comunes EN: las etiquetas de las horas
    // deben permanecer en inglés incluso si localStorage conserva un
    // paquete de labels viejo/incompleto y el modelo trae fallback español.
    if (scope === 'liturgy_of_hours' && Number(lang_id) === 4) {
      const englishHourLabels: Record<string, string> = {
        first_vespers: 'First Vespers',
        office_of_readings: 'Office of Readings',
        lauds: 'Lauds',
        midday_prayer: 'Daytime Prayer',
        vespers: 'Vespers',
      };
      if (englishHourLabels[sectionKey]) {
        return englishHourLabels[sectionKey];
      }
    }

    const definition = structuredModel?.scopes?.[scope]?.sections?.find(
      (section: any) => section.key === sectionKey
    );
    return structuredDefinitionLabel(definition, sectionKey);
  };

  const structuredSectionOrder = (scope: string, sectionKey: string) => {
    const definitions = structuredModel?.scopes?.[scope]?.sections || [];
    const index = definitions.findIndex(
      (section: any) => section.key === sectionKey
    );
    return index >= 0 ? index : Number.MAX_SAFE_INTEGER;
  };

  const structuredVariantDefinition = (variantKey: string) =>
    (values.common_meta?.official_variants || []).find(
      (variant: any) =>
        variant && typeof variant === 'object' && variant.key === variantKey
    );

  const structuredVariantLabel = (variantKey: string) => {
    if (!variantKey || variantKey === 'default') {
      return labelFromFiestas('common_variant_default', 'Texto general del común');
    }
    const definition = structuredVariantDefinition(variantKey);
    const labelKey = definition?.label_key || `common_variant_${variantKey}`;
    const fallback =
      definition?.label ||
      (variantKey === values.common_meta?.variant_key
        ? values.common_meta?.variant_label
        : null) ||
      variantKey.replace(/_/g, ' ');
    return labelFromFiestas(labelKey, fallback);
  };

  const structuredVariantOrder = (variantKey: string) => {
    if (!variantKey || variantKey === 'default') return 0;
    if (variantKey === values.common_meta?.variant_key) return 1;
    const variants = values.common_meta?.official_variants || [];
    const index = variants.findIndex(
      (variant: any) =>
        variant && typeof variant === 'object' && variant.key === variantKey
    );
    return index >= 0 ? index + 2 : Number.MAX_SAFE_INTEGER;
  };

  const structuredContentHtml = (item: any) => {
    const parts: string[] = [];
    const rubric = String(item?.rubric || '').trim();
    const hiddenTechnicalRubrics = new Set([
      'Conjunto de lecturas publicado por la fuente nativa para el Común de esta familia.',
      'Conjunto de lecturas publicado por la fuente nativa para el común de esta familia.',
    ]);

    if (rubric && !hiddenTechnicalRubrics.has(rubric)) {
      parts.push(`<p><em>${escapeHtml(rubric)}</em></p>`);
    }
    if (item.citation) {
      parts.push(`<p><strong>${escapeHtml(item.citation)}</strong></p>`);
    }
    if (item.content_html) {
      parts.push(item.content_html);
    }

    return parts.join('');
  };

  const sortedStructuredItems = (
    scope: 'mass' | 'lectionary' | 'liturgy_of_hours'
  ) =>
    verifiedStructuredItems
      .filter((item: any) => item.scope === scope)
      .sort((a: any, b: any) => {
        const variantOrderCompare =
          structuredVariantOrder(a.variant_key) -
          structuredVariantOrder(b.variant_key);
        if (variantOrderCompare !== 0) return variantOrderCompare;

        const variantCompare = String(a.variant_key).localeCompare(
          String(b.variant_key)
        );
        if (variantCompare !== 0) return variantCompare;

        if (scope === 'mass') {
          const formularyCompare =
            Number(a.option_order) - Number(b.option_order);
          if (formularyCompare !== 0) return formularyCompare;
        }

        const sectionCompare =
          structuredSectionOrder(scope, a.section_key) -
          structuredSectionOrder(scope, b.section_key);
        if (sectionCompare !== 0) return sectionCompare;

        return Number(a.option_order) - Number(b.option_order);
      });

  const renderMassScope = () => {
    const scoped = sortedStructuredItems('mass');
    if (scoped.length === 0) return null;

    const scopeDefinition = structuredModel?.scopes?.mass;
    const scopeLabel = structuredDefinitionLabel(scopeDefinition, 'Misa');
    const groups = new Map<string, any[]>();

    scoped.forEach((item: any) => {
      const optionOrder = Number(item.option_order) || 1;
      const key = `${item.variant_key || 'default'}::${optionOrder}`;
      const current = groups.get(key) || [];
      current.push(item);
      groups.set(key, current);
    });

    const groupEntries = Array.from(groups.entries());
    const activeKey =
      selectedMassGroupKey && groups.has(selectedMassGroupKey)
        ? selectedMassGroupKey
        : groupEntries[0]?.[0];
    const activeItems = activeKey ? groups.get(activeKey) || [] : [];
    const variantBuckets = new Map<string, Array<[string, any[]]>>();

    groupEntries.forEach(([groupKey, items]) => {
      const variantKey = items[0]?.variant_key || 'default';
      const current = variantBuckets.get(variantKey) || [];
      current.push([groupKey, items]);
      variantBuckets.set(variantKey, current);
    });

    const selectorButton = (
      groupKey: string,
      label: string,
      compact = false
    ) => {
      const active = groupKey === activeKey;
      return (
        <button
          key={groupKey}
          type="button"
          onClick={() => setSelectedMassGroupKey(groupKey)}
          style={{
            width: '100%',
            minHeight: compact ? 40 : 46,
            padding: compact ? '8px 10px' : '10px 12px',
            borderRadius: 8,
            border: active
              ? '1px solid var(--titulos)'
              : '1px solid rgba(191, 122, 28, 0.35)',
            background: active ? 'var(--titulos)' : 'rgba(255,255,255,0.58)',
            color: active ? '#fff' : 'inherit',
            fontWeight: active ? 700 : 600,
            cursor: 'pointer',
            lineHeight: 1.25,
          }}
        >
          {label}
        </button>
      );
    };

    const activeFirst = activeItems[0];
    const activeVariantKey = activeFirst?.variant_key || 'default';
    const activeVariantLabel =
      activeVariantKey !== 'default'
        ? structuredVariantLabel(activeVariantKey)
        : '';
    const activeVariantSize = variantBuckets.get(activeVariantKey)?.length || 1;
    const activeOptionOrder = Number(activeFirst?.option_order) || 1;
    const activeTitle = activeVariantLabel
      ? activeVariantSize > 1
        ? `${activeVariantLabel} · ${labelFromFiestas('structured_option', 'Opción')} ${activeOptionOrder}`
        : activeVariantLabel
      : groupEntries.length > 1
        ? `${labelFromFiestas('structured_option', 'Opción')} ${activeOptionOrder}`
        : '';

    return (
      <SectionAccordion title={scopeLabel}>
        {groupEntries.length > 1 && (
          <div style={{ marginBottom: 18 }}>
            {Array.from(variantBuckets.entries()).map(([variantKey, entries]) => {
              if (entries.length <= 1) return null;

              return (
                <div key={variantKey} style={{ marginBottom: 14 }}>
                  {variantKey !== 'default' && (
                    <div
                      style={{
                        margin: '0 0 8px',
                        fontWeight: 700,
                        color: 'var(--titulos)',
                      }}
                    >
                      {structuredVariantLabel(variantKey)}
                    </div>
                  )}
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
                      gap: 8,
                    }}
                  >
                    {entries.map(([groupKey, items]) =>
                      selectorButton(
                        groupKey,
                        `${labelFromFiestas('structured_option', 'Opción')} ${Number(items[0]?.option_order) || 1}`,
                        true
                      )
                    )}
                  </div>
                </div>
              );
            })}

            {Array.from(variantBuckets.entries()).some(([, entries]) => entries.length === 1) && (
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                  gap: 8,
                }}
              >
                {Array.from(variantBuckets.entries())
                  .filter(([, entries]) => entries.length === 1)
                  .map(([variantKey, entries]) => {
                    const [groupKey, items] = entries[0];
                    const label =
                      variantKey !== 'default'
                        ? structuredVariantLabel(variantKey)
                        : `${labelFromFiestas('structured_option', 'Opción')} ${Number(items[0]?.option_order) || 1}`;
                    return selectorButton(groupKey, label);
                  })}
              </div>
            )}
          </div>
        )}

        {activeTitle && (
          <div
            style={{
              margin: '2px 0 18px',
              paddingBottom: 8,
              borderBottom: '1px solid rgba(191, 122, 28, 0.24)',
              color: 'var(--titulos)',
              fontWeight: 700,
            }}
          >
            {activeTitle}
          </div>
        )}

        {activeItems.map((item: any) => (
          <div
            key={`${item.variant_key}-${item.section_key}-${item.option_order}`}
            style={{ margin: '0 0 24px' }}
          >
            <h4 style={{ margin: '0 0 10px' }}>
              {structuredSectionLabel('mass', item.section_key)}
            </h4>
            <div
              dangerouslySetInnerHTML={{ __html: structuredContentHtml(item) }}
            />
          </div>
        ))}
      </SectionAccordion>
    );
  };

  const renderLectionaryScope = () => {
    const scoped = sortedStructuredItems('lectionary');
    if (scoped.length === 0) return null;

    const scopeDefinition = structuredModel?.scopes?.lectionary;
    const scopeLabel = structuredDefinitionLabel(scopeDefinition, 'Leccionario');

    // Native Commons ES/EN/CA: show the Lectionary with the same option-selector
    // interaction used by Mass, but keep options independent inside each
    // reading section. option_order is section-local in the native source;
    // grouping option 1 across First Reading/Psalm/Gospel would invent a set.
    if ([1, 4, 9].includes(Number(lang_id))) {
      const sectionGroups = new Map<string, any[]>();

      scoped.forEach((item: any) => {
        const variantKey = item.variant_key || 'default';
        const key = `${variantKey}::${item.section_key}`;
        const current = sectionGroups.get(key) || [];
        current.push(item);
        current.sort(
          (a: any, b: any) =>
            (Number(a.option_order) || 1) - (Number(b.option_order) || 1)
        );
        sectionGroups.set(key, current);
      });

      const entries = Array.from(sectionGroups.entries()).sort(
        ([, aItems], [, bItems]) => {
          const a = aItems[0];
          const b = bItems[0];

          const variantCompare =
            structuredVariantOrder(a?.variant_key) -
            structuredVariantOrder(b?.variant_key);
          if (variantCompare !== 0) return variantCompare;

          return (
            structuredSectionOrder('lectionary', a?.section_key) -
            structuredSectionOrder('lectionary', b?.section_key)
          );
        }
      );

      return (
        <SectionAccordion title={scopeLabel}>
          {entries.map(([groupKey, items]) => {
            const first = items[0];
            const sectionKey = first?.section_key || '';
            const variantKey = first?.variant_key || 'default';
            const selectedOrder =
              selectedLectionaryOptions[groupKey] ??
              (Number(first?.option_order) || 1);
            const activeItem =
              items.find(
                (item: any) =>
                  (Number(item.option_order) || 1) === selectedOrder
              ) || first;

            return (
              <div
                key={groupKey}
                style={{
                  width: '100%',
                  margin: '0 0 26px',
                }}
              >
                <h4
                  style={{
                    margin: '0 0 10px',
                    color: 'var(--titulos)',
                  }}
                >
                  {structuredSectionLabel('lectionary', sectionKey)}
                  {variantKey !== 'default'
                    ? ` · ${structuredVariantLabel(variantKey)}`
                    : ''}
                </h4>

                {items.length > 1 && (
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns:
                        'repeat(auto-fit, minmax(128px, 1fr))',
                      gap: 8,
                      marginBottom: 14,
                    }}
                  >
                    {items.map((item: any) => {
                      const optionOrder = Number(item.option_order) || 1;
                      const active = optionOrder === selectedOrder;

                      return (
                        <button
                          key={`${groupKey}::${optionOrder}`}
                          type="button"
                          onClick={() =>
                            setSelectedLectionaryOptions((current) => ({
                              ...current,
                              [groupKey]: optionOrder,
                            }))
                          }
                          style={{
                            width: '100%',
                            minHeight: 40,
                            padding: '8px 10px',
                            borderRadius: 8,
                            border: active
                              ? '1px solid var(--titulos)'
                              : '1px solid rgba(191, 122, 28, 0.35)',
                            background: active
                              ? 'var(--titulos)'
                              : 'rgba(255,255,255,0.58)',
                            color: active ? '#fff' : 'inherit',
                            fontWeight: active ? 700 : 600,
                            cursor: 'pointer',
                            lineHeight: 1.25,
                          }}
                        >
                          {labelFromFiestas('structured_option', 'Opción')}{' '}
                          {optionOrder}
                        </button>
                      );
                    })}
                  </div>
                )}

                <div
                  dangerouslySetInnerHTML={{
                    __html: structuredContentHtml(activeItem),
                  }}
                />
              </div>
            );
          })}
        </SectionAccordion>
      );
    }

    return (
      <SectionAccordion title={scopeLabel}>
        {scoped.map((item: any) => {
          const sectionLabel = structuredSectionLabel(
            'lectionary',
            item.section_key
          );
          const variant =
            item.variant_key && item.variant_key !== 'default'
              ? ` · ${structuredVariantLabel(item.variant_key)}`
              : '';
          const optionOrder = Number(item.option_order) || 1;
          const option =
            optionOrder > 1
              ? ` · ${labelFromFiestas('structured_option', 'Opción')} ${optionOrder}`
              : '';

          return (
            <div
              key={`lectionary-${item.variant_key}-${item.section_key}-${item.option_order}`}
              style={{ width: '100%', marginBottom: 8 }}
            >
              <BasicAccordion
                titulo={`${sectionLabel}${variant}${option}`}
                contenido={structuredContentHtml(item)}
              />
            </div>
          );
        })}
      </SectionAccordion>
    );
  };

  const structuredHourItems = sortedStructuredItems('liturgy_of_hours');

  const visibleLegacyHourSections =
    isCanonicalCommon && structuredHourItems.length > 0
      ? []
      : hourSections
          .map(([field, label]) => ({
            field,
            label,
            content: getBioHTML(field),
          }))
          .filter((section) => validar(section.content));

  const renderLiturgyOfHours = () => {
    if (structuredHourItems.length > 0) {
      const scopeDefinition = structuredModel?.scopes?.liturgy_of_hours;
      const scopeLabel = structuredDefinitionLabel(
        scopeDefinition,
        'Liturgia de las Horas'
      );

      return (
        <SectionAccordion title={scopeLabel}>
          {structuredHourItems.map((item: any) => {
            const variant =
              item.variant_key && item.variant_key !== 'default'
                ? ` · ${structuredVariantLabel(item.variant_key)}`
                : '';

            return (
              <div
                key={`hours-${item.variant_key}-${item.section_key}-${item.option_order}`}
                style={{ width: '100%', marginBottom: 8 }}
              >
                <BasicAccordion
                  titulo={`${structuredSectionLabel(
                    'liturgy_of_hours',
                    item.section_key
                  )}${variant}`}
                  contenido={structuredContentHtml(item)}
                />
              </div>
            );
          })}
        </SectionAccordion>
      );
    }

    if (visibleLegacyHourSections.length === 0) return null;

    return (
      <SectionAccordion
        title={labelFromFiestas(
          'structured_liturgy_of_hours',
          'Liturgia de las Horas'
        )}
      >
        {visibleLegacyHourSections.map((section) => (
          <div key={section.field} style={{ width: '100%', marginBottom: 8 }}>
            <BasicAccordion
              titulo={readableAccordionTitle(getFiestasLabel(section.label))}
              contenido={section.content}
            />
          </div>
        ))}
      </SectionAccordion>
    );
  };

  const hasVisibleLanguageContent =
    verifiedStructuredItems.length > 0
    || visibleLegacyHourSections.length > 0
    || legacyReadingSections.some(([field]) => validar(getBioHTML(field)));

  const showLanguageUnavailable =
    isCanonicalCommon
    && Number(lang_id) !== 1
    && !hasVisibleLanguageContent;

  return (
    <div id="SantoralView" className="module_container">
      <div className="module_topBar" style={{ padding: 0 }}>
        <TopBar title={getFiestasLabel('propios_titulo')} />
        <Toolbar />
      </div>

      <div className='module_contentSantoral'>
        {values.image && (
          <div id='mv_image' style={{ padding: 20 }}>
            <img src={values.image} alt={getTitulo()} />
          </div>
        )}

        <h2><strong>{getTitulo()}</strong></h2>

        {values.common_meta?.variant_key && (
          <div style={{ maxWidth: 760, margin: '0 auto 20px', textAlign: 'center' }}>
            {structuredVariantLabel(values.common_meta.variant_key)}
          </div>
        )}

        {renderMassScope()}
        {renderLectionaryScope()}
        {renderLiturgyOfHours()}

        {showLanguageUnavailable && (
          <div style={{ width: '100%', padding: '18px 0', textAlign: 'center' }}>
            {getFiestasLabel('common_content_unavailable')}
          </div>
        )}

        {legacyReadingSections.map(([field, label]) => {
          const content = getBioHTML(field);
          if (!validar(content)) return null;

          return (
            <div key={field} style={{ width: '100%', marginBottom: 12 }}>
              <BasicAccordion
                titulo={readableAccordionTitle(getFiestasLabel(label))}
                contenido={content}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}
